'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Clean Beauty's Plastic Loophole: Why 1,600 Banned Ingredients Don't Include Plastic"
      author="MicroPlastics Research Desk"
      date="July 31, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/cosmetics.webp"
      heroImageAlt="Clean beauty microplastics loophole Clean at Sephora banned ingredients list plastic 2026"
    >
      <BlogLastUpdated iso="2026-07-31" formatted="July 31, 2026" />

      <BlogTLDR>
        <strong>Every major clean-beauty standard bans a long list of
        ingredients. None of them bans plastic.</strong> &ldquo;Clean at
        Sephora&rdquo; excludes roughly 50 ingredient categories,
        parabens, sulfates, phthalates, formaldehyde donors, oxybenzone,
        hydroquinone, coal tar. Kylie Cosmetics&apos; 2021 relaunch
        banned <strong>more than 1,600</strong> ingredients. Neither
        excludes <strong>synthetic polymer microparticles</strong>,
        the nylon-12, PMMA, polyethylene and PET that our{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">audit found in 24 of 81 formulas</Link>{' '}
        across four cult brands. This is not brands cheating. It is a{' '}
        <strong>category error baked into how clean beauty was
        designed</strong>: these lists screen for <em>toxicity</em>, and
        the problem with microplastics is <em>persistence</em>. A
        chemically inert particle passes every toxicity screen ever
        written. Meanwhile EU law does not touch make-up until{' '}
        <strong>17 October 2035</strong>. Until then, the only thing
        standing between you and a plastic microsphere is reading the
        label.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>Clean beauty screens for hazard. Microplastics are a persistence problem.</strong> That single mismatch explains the entire loophole.</>,
          <>Polymer particles are <strong>chemically inert</strong>, which is exactly why they sail through a toxicity-based exclusion list. Being inert is what gets them past the filter.</>,
          <>&ldquo;Clean at Sephora&rdquo; expanded in December 2025 to cover <strong>packaging</strong>, adding PFAS, bisphenols and mineral oils. Synthetic polymer microparticles still were not added to the formula list.</>,
          <>There is <strong>no widely adopted &ldquo;microplastic free&rdquo; certification</strong> in cosmetics. Not one of the ten purity badges Kylie displays covers it, and no competitor offers one either.</>,
          <>The one authority that <em>does</em> regulate this is the EU, and it gave make-up until <strong>October 2035</strong>, rinse-off products until 2027 and leave-on until 2029.</>,
          <>Practical consequence: <strong>certifications cannot answer this question for you.</strong> The ingredient list can, and it takes about ten seconds once you know the five words to look for.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The thing nobody says out loud
      </h2>
      <p>
        Clean beauty is now a mainstream retail category. Sephora
        launched its seal in 2018 and it now covers more than 3,000
        products across 250-plus brands. The promise is legible from
        across a shop floor: <em>we removed the bad stuff.</em>
      </p>
      <p>
        Read the actual exclusion lists and a pattern appears
        immediately. Parabens, preservatives. Sulfates, surfactants.
        Phthalates, plasticisers and fragrance carriers. Formaldehyde
        donors. Oxybenzone. Hydroquinone. Coal tar. Triclosan. Heavy
        metals.
      </p>
      <p>
        Every one of those is on the list for the same reason:{' '}
        <strong>a toxicological concern.</strong> Something about the
        molecule is suspected of doing something to a biological system,
        endocrine disruption, sensitisation, carcinogenicity, organ
        toxicity.
      </p>
      <p>
        Now consider nylon-12. It is a solid, chemically inert polymer
        sphere. It does not dissolve. It does not react. It does not
        bind a hormone receptor. It is not absorbed. Put it through
        every screen that built the clean-beauty lists and it passes all
        of them, <strong>because it is inert.</strong>
      </p>
      <p>
        That is the loophole in one sentence: <strong>clean beauty asks
        &ldquo;is this molecule toxic?&rdquo; and microplastics answer
        &ldquo;no, and I will still be here in four hundred
        years.&rdquo;</strong>
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the standards actually cover
      </h2>

      <BlogComparisonTable
        caption="Major clean-beauty standards and whether they exclude synthetic polymer microparticles"
        columns={[
          { key: 'std', label: 'Standard' },
          { key: 'covers', label: 'What it excludes' },
          { key: 'mp', label: 'Excludes microplastics?' },
        ]}
        rows={[
          { std: 'Clean at Sephora', covers: 'Roughly 50 ingredient categories: parabens, sulfates (SLS/SLES), phthalates, formaldehyde and donors, oxybenzone, hydroquinone, triclosan, coal tar, mineral oil, heavy metals. BHA/BHT restricted rather than banned. Extended to packaging in Dec 2025 (PFAS, bisphenols).', mp: <><strong>No</strong></> },
          { std: 'Kylie Cosmetics (2021 Coty relaunch)', covers: 'Announced as banning 1,600+ ingredients, including animal oils, parabens and gluten.', mp: <><strong>No</strong></> },
          { std: <>Typical “vegan” claim</>, covers: 'Animal-derived ingredients. Polymers are synthetic, so a plastic-heavy product is trivially vegan.', mp: <><strong>No</strong></> },
          { std: <>Typical “cruelty free” claim</>, covers: 'Animal testing practices. Says nothing about composition at all.', mp: <><strong>No</strong></> },
          { std: <>“Talc free”, “paraben free”, “sulfate free”</>, covers: 'Exactly the one named ingredient.', mp: <><strong>No</strong></> },
          { std: 'EU REACH Annex XVII entry 78 (Reg. 2023/2055)', covers: 'Solid, insoluble synthetic polymer particles under 5 mm at ≥0.01% w/w.', mp: <><strong>Yes</strong> — but rinse-off from Oct 2027, leave-on Oct 2029, make-up Oct 2035</> },
        ]}
      />
      <p>
        Note the last row. <strong>The only standard in that table that
        addresses microplastics at all is a law, not a certification</strong>,
        and it is a law that will not affect a lipstick for another nine
        years.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        In defence of clean beauty, briefly
      </h2>
      <p>
        It would be cheap to end there, so here is the other side.
      </p>
      <p>
        Clean-beauty standards were never designed to be environmental
        standards. They are consumer-safety standards, and judged on
        that basis they have done real work: they moved the industry off
        several genuinely questionable preservatives and UV filters
        faster than regulation would have.
      </p>
      <p>
        There is also a real scientific defence of polymer particles
        specifically. <strong>Inertness is a safety feature.</strong> The
        reason PMMA is used in bone cement and intraocular lenses is
        that the body ignores it. A formulator who swaps nylon-12 for
        something bio-derived may be trading a persistent-but-inert
        particle for a reactive one, and that is not automatically an
        improvement for the person wearing it.
      </p>
      <p>
        And a US retailer&apos;s &ldquo;clean&rdquo; seal has no
        obligation to encode an EU environmental restriction. Sephora is
        not breaking a promise it made.
      </p>
      <p>
        The criticism that survives all of that is narrower and, we
        think, fair: <strong>the word &ldquo;clean&rdquo; does not
        communicate &ldquo;we screened for toxicity only.&rdquo;</strong>{' '}
        It communicates <em>this product is not full of bad stuff</em>.
        Shoppers reasonably read it as covering plastic. It does not, and
        nothing on the packaging tells them so.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why 2035?
      </h2>
      <p>
        Make-up got the longest transition period in the entire EU
        microplastics restriction. Twelve years from adoption. It is
        worth understanding why, because it is not simply lobbying.
      </p>
      <p>
        In a rinse-off scrub, a plastic microbead was doing a job that
        apricot kernel or salt can do about as well. Substitution was
        easy, which is why microbeads were banned outright back in 2023.
      </p>
      <p>
        In colour cosmetics the polymer <em>is</em> the performance.
        Nylon-12 spheres are the blur. PMMA is the soft focus. PET is the
        sparkle. Polyethylene is what makes a pencil a pencil. Silicone
        elastomer is the long-wear finish. Reformulating means
        re-engineering the product, then re-testing stability and safety
        across every shade, and colour cosmetics have a lot of shades.
      </p>
      <p>
        So 2035 is a real engineering timeline, not purely a
        concession. It is also, from a shopper&apos;s point of view,{' '}
        <strong>nine more years in which the label is the only tool you
        have.</strong>
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        How to read a label in ten seconds
      </h2>
      <p>
        You do not need to memorise a chemistry syllabus. You need five
        words and one distinction.
      </p>
      <p><strong>Scan the first five to eight ingredients for these:</strong></p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Nylon-12</strong> or <strong>Nylon-6</strong> &mdash; blurring microspheres. Powders.</li>
        <li><strong>Polymethyl Methacrylate</strong> or <strong>PMMA</strong>, and <strong>Methyl Methacrylate Crosspolymer</strong> &mdash; acrylic beads. Powders, primers.</li>
        <li><strong>Polyethylene</strong> &mdash; wax hardener. Pencils, sticks, balms. (Careful: <em>polyethylene glycol</em> / PEG is a different, water-soluble thing and does not count.)</li>
        <li><strong>Polyethylene Terephthalate</strong> / <strong>PET</strong> &mdash; glitter. Lip toppers, shadows, highlighters.</li>
        <li><strong>Acrylates Copolymer</strong> / <strong>Acrylates Crosspolymer</strong> &mdash; film-formers and thickeners. Skincare, cleansers, long-wear.</li>
        <li><strong>Dimethicone/Vinyl Dimethicone Crosspolymer</strong> &mdash; silicone elastomer. Solid particle, counts.</li>
      </ul>
      <p><strong>And the distinction that most articles get wrong:</strong></p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Dimethicone</strong> on its own is a <em>liquid</em> silicone. Not a microplastic. The EU definition requires a solid particle.</li>
        <li><strong>PEG-</strong> and <strong>PPG-</strong> compounds are water-soluble. Not microplastics.</li>
        <li><strong>Hydrogenated polyisobutene, polybutene</strong> are liquid synthetic polymers. Petrochemical, yes. Microplastic, no.</li>
      </ul>
      <p>
        If a source tells you dimethicone is the microplastic in your
        makeup, it has not read the regulation, and it is burying the
        ingredients that actually qualify.
      </p>

      <BlogAppCTA
        headline="Stop trusting the badge, check the list"
        sub="Vegan, cruelty free, clean, talc free, paraben free: none of them cover plastic. Scan any product and the app reads the full ingredient list, separates solid polymer particles from liquid silicones and soluble PEGs, and shows a cleaner same-format swap."
        buttonLabel="Scan a product"
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What we found when we actually checked
      </h2>
      <p>
        We applied exactly this test to four of the most talked-about
        beauty brands in the world, reading the ingredient lists they
        publish themselves:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><Link href="/blog/microplastics-in-makeup-brands-audited" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">The full audit</Link> &mdash; 24 of 81 formulas, and the method.</li>
        <li><Link href="/blog/does-fenty-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Fenty Beauty</Link> &mdash; PMMA in the blurring powder, PET glitter in a lip topper.</li>
        <li><Link href="/blog/does-huda-beauty-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Huda Beauty</Link> &mdash; Nylon-12 listed fifth in Easy Bake.</li>
        <li><Link href="/blog/does-rhode-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">rhode</Link> &mdash; the Peptide Lip Treatment passes, the cleanser does not.</li>
        <li><Link href="/blog/does-kylie-cosmetics-have-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">Kylie Cosmetics</Link> &mdash; ten purity badges, and the one that is missing.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>The full INCI list from the barcode, matched to the exact SKU and shade.</>,
          <>Solid polymer particles flagged separately from liquid silicones and water-soluble PEGs, because conflating them is the most common error in this space.</>,
          <>Which marketing claims the product carries, and what each one actually covers.</>,
          <>Whether the product is rinse-off, leave-on or make-up, and therefore which EU deadline applies (2027, 2029 or 2035).</>,
          <>A cleaner same-format, same-budget swap.</>,
        ]}
      />

      <BlogFAQ
        items={[
          {
            question: 'Does "Clean at Sephora" mean a product is free from microplastics?',
            answer:
              'No. The Clean at Sephora standard excludes roughly 50 ingredient categories, including parabens, sulfates, phthalates, formaldehyde donors, oxybenzone, hydroquinone, triclosan, coal tar and heavy metals, and was extended in December 2025 to cover packaging materials such as PFAS and bisphenols. Synthetic polymer microparticles are not on the exclusion list. A product carrying the Clean at Sephora seal can contain nylon-12, PMMA, polyethylene or PET.',
          },
          {
            question: 'Why do clean-beauty standards ignore microplastics?',
            answer:
              'Because they were built to screen for toxicity, and microplastics are a persistence problem rather than a toxicity problem. Every ingredient on a typical clean-beauty exclusion list is there because of a suspected biological effect: endocrine disruption, sensitisation, carcinogenicity, organ toxicity. Polymer microparticles are chemically inert, so they pass those screens by design. Ironically, the property that makes them environmentally troubling, that nothing breaks them down, is the same property that makes them look harmless to a toxicity-based standard.',
          },
          {
            question: 'Is there a microplastic-free certification for cosmetics?',
            answer:
              'There is no widely adopted one. Some smaller eco-labels and a handful of European retailer standards address polymer content, but nothing with the reach of Clean at Sephora, Leaping Bunny or the common vegan marks covers synthetic polymer microparticles. In practice the only reliable check available to a shopper today is reading the ingredient list, which is why we published the five words to look for rather than a list of certified brands.',
          },
          {
            question: 'Is "vegan" makeup free from plastic?',
            answer:
              'No, and often the opposite. A vegan claim means no animal-derived ingredients. Synthetic polymers are made from petrochemicals, not animals, so nylon-12, PMMA, polyethylene and PET are all perfectly vegan. A brand replacing beeswax or carmine with synthetic alternatives can increase its polymer content while strengthening its vegan credentials. The two claims are unrelated.',
          },
          {
            question: 'When will the law actually ban microplastics in makeup?',
            answer:
              '17 October 2035 in the EU, for lip products, nail products and make-up. Rinse-off products such as cleansers and shampoos face the restriction on 17 October 2027, and leave-on skincare on 17 October 2029. Rinse-off microbeads have been banned since October 2023. The staggering reflects how hard substitution is: in a scrub the bead was replaceable with salt or kernel powder, whereas in colour cosmetics the polymer is usually delivering the core performance.',
          },
          {
            question: 'Is dimethicone a microplastic?',
            answer:
              'No. Commission Regulation (EU) 2023/2055 requires a synthetic polymer microparticle to be a solid particle that is poorly water-soluble, under 2 g/L. Dimethicone is a liquid silicone fluid and falls outside that definition, as do PEG and PPG compounds, which are water-soluble. Silicone elastomer crosspolymers such as dimethicone/vinyl dimethicone crosspolymer are a different case, those are solid swollen particles and they do count.',
          },
          {
            question: 'Should I throw out my makeup?',
            answer:
              'No. There is no published evidence that polymer particles in cosmetics harm the person wearing them, and the particles used are far too large to cross intact skin. Throwing away working products creates waste for no health benefit. The sensible approach is to replace things as they run out, and to prioritise: loose setting powder first, because it is the most polymer-dense and the most inhalable format, then rinse-off products, because that is where the environmental case is strongest and where the law arrives first.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'European Commission',
            year: 2023,
            title: 'Commission Regulation (EU) 2023/2055 amending Annex XVII to REACH as regards synthetic polymer microparticles',
            publisher: 'Official Journal of the European Union (EUR-Lex, CELEX 32023R2055)',
            url: 'https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX%3A32023R2055',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Microplastics: restriction on intentionally added microplastics',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/hot-topics/microplastics',
          },
          {
            authors: 'Sephora',
            year: 2023,
            title: 'Sephora Public Chemicals Policy and the Clean at Sephora ingredient exclusion standard',
            publisher: 'Sephora',
            url: 'https://www.inside-sephora.com/',
          },
          {
            authors: 'Coty Inc.',
            year: 2021,
            title: 'Coty announces the relaunch of Kylie Cosmetics with new and improved clean and vegan formulas',
            publisher: 'Coty Inc. / Business Wire, 1 July 2021',
            url: 'https://www.coty.com/news',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
